import React, { forwardRef, memo } from 'react';
import styled from '@emotion/styled';
import { useButton, useI18n } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import useHopOnChange from '../../Hooks/useHopOnChange';
import cartIcon from '../../../../Assets/icons/EPOS/shopping_cart.svg';
import CheckoutButton from './CheckoutButton';

/** Height of the bar - the cart drawer sits right on top of it. */
export const CART_BAR_HEIGHT = 132;

/**
 * Portrait only - the landscape sheet docks the cart in CartPanel instead.
 * Pinned to the bottom of the screen, full width. Flies up once, when it
 * mounts - the parent only mounts it once the cart holds something, so the
 * first added item is what brings it in.
 *
 * With the `is-cart-open` class (toggled imperatively by the parent) the cart
 * button collapses and the checkout button slides left to fill the bar.
 */
const Bar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${CART_BAR_HEIGHT}px;
  display: flex;
  flex-direction: row;
  z-index: 3;
  box-shadow: 0 -12px 40px rgba(0, 0, 0, 0.18);
  animation: eposCartBarFlyIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;

  /* Out of the way while an item's detail pane is open (class set on the layer
     by Menu). The fly-in animation must be switched off here - its 'both'
     fill would otherwise pin transform at the final keyframe and win over this
     rule. Removing the class re-triggers the animation, so the bar flies back in. */
  .is-detail-open & {
    animation: none;
    transform: translate3d(0, 100%, 0);
  }

  /* Checkout starting: follows the sheet down, and stays down once gone - the
     menu itself stays mounted underneath the checkout screen, so without an
     explicit "done" state the bar would fall back to its fly-in rule above
     and pop back into view the moment the exit class is removed. */
  .epos-menu-exit-active & {
    animation: eposCartBarFlyOut 0.32s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .epos-menu-exit-done & {
    animation: none;
    transform: translate3d(0, 100%, 0);
  }

  @keyframes eposCartBarFlyOut {
    from {
      transform: translate3d(0, 0, 0);
    }
    to {
      transform: translate3d(0, 100%, 0);
    }
  }

  @keyframes eposCartBarFlyIn {
    from {
      transform: translate3d(0, 100%, 0);
    }
    to {
      transform: translate3d(0, 0, 0);
    }
  }
`;

/** Shared shape: square corners, no gap, each takes half the width. */
const BarButton = styled.button`
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  appearance: none;
  border: none;
  border-radius: 0;
  margin: 0;
  padding: 0 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  font-family: var(--eposFontFamily);
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.2px;
  transition: filter 0.15s ease-out, flex 0.4s cubic-bezier(0.22, 1, 0.36, 1),
    padding 0.4s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease-out;

  &.is-touching {
    filter: brightness(0.92);
  }
`;

const CartButton = styled(BarButton)`
  background: var(--colorWhite);
  color: var(--colorBlueGray1200);

  .is-cart-open & {
    flex: 0 0 0;
    padding: 0;
    opacity: 0;
    pointer-events: none;
  }
`;

/** The shared button, squared off to fill its half of the bar. */
const BarCheckout = styled(CheckoutButton)`
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  border-radius: 0;
  padding: 0 36px;
  font-size: 32px;

  .epos-checkout-arrow {
    width: 36px;
    height: 36px;
  }
`;

/**
 * Item count in a light grey circle, right of the "Cart" label. Hops every
 * time the number changes (useHopOnChange) - the shopper's eye is drawn to
 * the running order without a modal or toast.
 */
const Count = styled.span`
  min-width: 50px;
  height: 50px;
  padding: 0 14px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 800;
  background: var(--colorBlueGray100);
  color: var(--colorBlueGray1200);
  will-change: transform;
`;

interface Props {
  /** Number of items in the cart, shown in the circle. */
  count: number;
  /** Formatted order total after discounts, shown on the checkout button. */
  total: string;
  /** Formatted total before discounts - struck through on the button when it differs. */
  originalTotal?: string;
  onOpenCart: () => void;
  onCheckout: () => void;
  /** Nothing to pay for (empty cart or zero total) - the checkout button is shown but inert. */
  disabled?: boolean;
}

const CartBar = forwardRef<HTMLDivElement, Props>(
  ({ count, total, originalTotal, onOpenCart, onCheckout, disabled = false }, ref) => {
    const { i18n } = useI18n();

    const countRef = useHopOnChange<HTMLSpanElement>(count);

    const {
      touching: cartTouching,
      disabled: cartDisabled,
      disabledToast: cartToast,
      ...cartButton
    } = useButton({ onClick: onOpenCart });

    return (
      <Bar ref={ref} data-testid="epos-cart-bar">
        <CartButton
          {...cartButton}
          data-testid="epos-cart-button"
          className={cartTouching ? 'is-touching' : undefined}>
          <Icon src={cartIcon} color="var(--colorBlueGray1200)" width="36px" height="36px" />
          {i18n('Express.Cart')}
          <Count ref={countRef} data-testid="epos-cart-count">
            {count}
          </Count>
        </CartButton>
        <BarCheckout
          total={total}
          originalTotal={originalTotal}
          onCheckout={onCheckout}
          disabled={disabled}
        />
      </Bar>
    );
  },
);

CartBar.displayName = 'CartBar';

export default memo(CartBar);
